import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, FileText, Trash2, AlertTriangle, FileCheck, ChevronDown, Check, Tag } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { DocumentAttachment } from '@/types/domain';

interface DocumentUploaderProps {
  documents: DocumentAttachment[];
  onUpload: (file: File, category: string) => Promise<void>;
  onDelete: (documentId: string) => Promise<void>;
  disabled?: boolean;
  maxSizeMb?: number;
}

const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.png', '.jpg', '.jpeg'];
const CATEGORIES = [
  { label: 'Technical Spec', value: 'TechnicalSpec' },
  { label: 'Quotation', value: 'Quotation' },
  { label: 'Drawing', value: 'Drawing' },
  { label: 'Estimate', value: 'Estimate' },
  { label: 'Approval Document', value: 'ApprovalDoc' },
  { label: 'Other', value: 'Other' },
];

export const DocumentUploader: React.FC<DocumentUploaderProps> = ({
  documents,
  onUpload,
  onDelete,
  disabled = false,
  maxSizeMb = 10,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('TechnicalSpec');
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsCategoryOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setErrorMessage(null);

    // Extension Check
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setErrorMessage(`Invalid file format '${ext}'. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Size Check
    const maxSizeBytes = maxSizeMb * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      setErrorMessage(`File exceeds max permitted size of ${maxSizeMb}MB.`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    try {
      setIsUploading(true);
      await onUpload(file, selectedCategory);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch {
      setErrorMessage('Failed to upload document. Please retry.');
    } finally {
      setIsUploading(false);
    }
  };

  const selectedCategoryLabel =
    CATEGORIES.find((c) => c.value === selectedCategory)?.label || selectedCategory;

  return (
    <div className="space-y-4 font-heading">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-2xl border border-border/80 bg-muted/20">
        <div className="space-y-1">
          <h4 className="text-xs font-extrabold uppercase tracking-wider text-foreground flex items-center gap-1.5">
            <Upload className="h-4 w-4 text-primary" /> Attach Supporting Documents
          </h4>
          <p className="text-xs text-foreground-secondary">
            Upload PDF, Word, Excel, or images (Max {maxSizeMb}MB). Virus & malware scan active.
          </p>
        </div>

        {!disabled && (
          <div className="flex items-center gap-2.5 shrink-0">
            {/* Proper Category Pill Dropdown Button matching Upload File pill */}
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                className="h-9 px-3.5 rounded-xl border border-border bg-surface hover:bg-muted/40 text-xs font-extrabold text-foreground flex items-center gap-2 transition-all cursor-pointer shadow-2xs focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                <Tag className="h-3.5 w-3.5 text-primary" />
                <span className="text-[11px] font-semibold text-foreground-muted">Doc:</span>
                <span className="text-primary font-bold">{selectedCategoryLabel}</span>
                <ChevronDown
                  className={`h-3.5 w-3.5 text-foreground-muted transition-transform duration-150 ${
                    isCategoryOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              <AnimatePresence>
                {isCategoryOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 6, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 6, scale: 0.95 }}
                    transition={{ duration: 0.15, ease: 'easeOut' }}
                    className="absolute right-0 top-full mt-1.5 z-30 min-w-[210px] p-1.5 rounded-2xl border border-border/80 bg-surface dark:bg-[#121526] shadow-xl space-y-0.5"
                  >
                    <div className="px-3 py-1.5 text-[10px] font-bold text-foreground-muted uppercase tracking-wider border-b border-border/50">
                      Select Document Category
                    </div>
                    {CATEGORIES.map((c) => {
                      const isSelected = selectedCategory === c.value;
                      return (
                        <button
                          key={c.value}
                          type="button"
                          onClick={() => {
                            setSelectedCategory(c.value);
                            setIsCategoryOpen(false);
                          }}
                          className={`w-full px-3 py-2 rounded-xl text-left text-xs font-bold flex items-center justify-between transition-colors cursor-pointer ${
                            isSelected
                              ? 'bg-primary/10 text-primary font-black'
                              : 'text-foreground hover:bg-muted/60'
                          }`}
                        >
                          <span>{c.label}</span>
                          {isSelected && <Check className="h-3.5 w-3.5 text-primary" />}
                        </button>
                      );
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              className="hidden"
              accept={ALLOWED_EXTENSIONS.join(',')}
            />

            {/* Upload File Pill Button */}
            <Button
              type="button"
              size="sm"
              isLoading={isUploading}
              onClick={() => fileInputRef.current?.click()}
              className="h-9 px-4 rounded-xl text-xs font-extrabold cursor-pointer shadow-2xs"
            >
              <Upload className="h-3.5 w-3.5 mr-1.5" /> Upload File
            </Button>
          </div>
        )}
      </div>

      {errorMessage && (
        <div className="p-3.5 rounded-2xl border border-destructive/30 bg-destructive/10 text-destructive text-xs font-bold flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}

      {documents.length > 0 ? (
        <div className="divide-y divide-border/60 border border-border/80 rounded-2xl bg-surface overflow-hidden">
          {documents.map((doc) => (
            <div
              key={doc.id}
              className="p-3.5 flex items-center justify-between gap-3 hover:bg-muted/30 transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-9 w-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <FileText className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-xs font-bold text-foreground truncate">{doc.fileName}</p>
                    <Badge variant="outline" className="text-[10px] font-mono font-bold uppercase">
                      {doc.category}
                    </Badge>
                  </div>
                  <p className="text-[11px] text-foreground-secondary mt-0.5">
                    {(doc.fileSize / 1024).toFixed(1)} KB • Uploaded by {doc.uploadedByName} on{' '}
                    {new Date(doc.uploadedAt).toLocaleDateString()}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <span className="text-[11px] text-emerald-600 dark:text-emerald-400 font-bold flex items-center gap-1">
                  <FileCheck className="h-3.5 w-3.5" /> Scanned
                </span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => onDelete(doc.id)}
                    className="p-1.5 rounded-lg text-foreground-secondary hover:text-rose-500 hover:bg-rose-500/10 transition-colors cursor-pointer"
                    title="Remove document"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="p-8 text-center border border-dashed rounded-2xl border-border bg-surface/30">
          <FileText className="h-8 w-8 mx-auto text-foreground-muted mb-2" />
          <p className="text-xs font-bold text-foreground">No supporting documents attached yet</p>
          <p className="text-[11px] text-foreground-secondary mt-0.5">
            Attach vendor quotes, specifications, or CAPEX justification files to accelerate approval.
          </p>
        </div>
      )}
    </div>
  );
};
